import { FormEditActions } from "@arelia-components/form/FormEditActions";
import { Box, Card, CardContent, CardHeader } from "@mui/material";
import { useEffect, useState } from "react";
import SpinnerWrapper from "@arelia-components/ui-atoms/SpinnerWrapper";
import { Subscription, SubscriptionPlan, SubscriptionPlanName } from "@arelia-types/subscriptions";
import EditPlan from "./components/EditPlan";
import NoSubscription from "./components/NoSubscription";
import { ArelTooltip } from "@arelia-components/ui-atoms/Tooltip";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SubscriptionInfoUser from "./components/SubscriptionInfo/SubscriptionInfoUser";
import SubscriptionInfoAdmin from "./components/SubscriptionInfo/SubscriptionInfoAdmin";
import { useLocation } from "react-router-dom";

type Props = {
  isLoading: boolean;
  isAdmin: boolean;
  changePlanAvailable: boolean;
  subscription: Subscription | undefined;
  submitNewPlan: (plan: SubscriptionPlan, expDate: Date) => void;
};

export default function SubscriptionDetailsPresenter({ isLoading, subscription, isAdmin, changePlanAvailable, submitNewPlan }: Props) {
  const [editing, setEditing] = useState(false);
  const [plan, setPlan] = useState<SubscriptionPlan>();
  const tomorrowDate = new Date(new Date().setDate(new Date().getDate() + 1));
  const [expDate, setExpDate] = useState<Date>(tomorrowDate);

  const location = useLocation();
  const isInHQ = location.pathname.includes("/hq/");

  useEffect(() => setPlan(subscription?.plan), [subscription]);

  const handleEdit = () => {
    setEditing(true);
  };
  const handleSubmit = () => {
    setEditing(false);
    submitNewPlan(plan as Exclude<SubscriptionPlan, undefined>, expDate);
  };
  const handleReset = () => {
    setEditing(false);
  };

  const canSetFreePlan = !subscription || subscription?.plan === SubscriptionPlanName.Starter || subscription?.plan === null || !subscription.active;
  const headerActions = isAdmin && isInHQ ? <Box sx={styles.actionsWrapper}>
    <FormEditActions
      edit={editing}
      disabled={!canSetFreePlan}
      handleEdit={handleEdit}
      handleSubmit={handleSubmit}
      handleReset={handleReset}
    />
    {!canSetFreePlan && <ArelTooltip title={"It is possible to set free plan only if user does not have other paid active plan at the moment."}>
      <InfoOutlinedIcon sx={styles.hintIcon} color="warning" />
    </ArelTooltip>}
  </Box> : null;

  return <Card sx={styles.card}>
    <CardHeader
      title="Subscription details"
      action={headerActions}
    />
    <SpinnerWrapper loading={isLoading}>
      <CardContent sx={styles.card.content}>
        {subscription?.plan && !editing && isAdmin && isInHQ && (
          <SubscriptionInfoAdmin
            subscription={subscription}
            removeSubAvailable={true}
          />
        )}

        {subscription?.plan && !editing && !(isAdmin && isInHQ) && (
          <SubscriptionInfoUser
            subscription={subscription}
            changePlanAvailable={changePlanAvailable}
          />
        )}

        {editing && <EditPlan plan={plan} setPlan={setPlan} expDate={expDate} setExpDate={setExpDate} />}

        {!subscription?.plan && !editing && <NoSubscription />}
      </CardContent>
    </SpinnerWrapper>
  </Card>;
}

const styles = {
  card: {
    gridColumn: "span 6",
    gridRow: "span 9",
    content: {
      paddingTop: "20px"
    }
  },
  actionsWrapper: {
    display: "flex",
    alignItems: "center"
  },
  hintIcon: {
    cursor: "pointer"
  }
}
